import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, CheckCircle2, ExternalLink, Loader2, Sparkles } from 'lucide-react';
import { apiErrorMessage } from '../lib/planLimit';
import {
  FieldInput, PageShell, Panel, ProgressBar, StatusBadge, primaryButton, secondaryButton, useApi, useTone,
} from '../components/demarches/ui';

const isInternal = (url) => url && url.startsWith('/');

export const DemarchesPage = () => {
  const api = useApi();
  const tone = useTone();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [event, setEvent] = useState(null);
  const [eventValues, setEventValues] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get('/demarches').then(setData).catch(() => toast.error('Démarches indisponibles'));
  }, [api]);

  const start = async (procedureId) => {
    setBusy(true);
    try {
      const d = await api.post('/dossiers', { procedure_id: procedureId });
      navigate(`/dashboard/demarches/${d.dossier.id}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Impossible d'ouvrir le dossier"));
      setBusy(false);
    }
  };

  const declare = async (type) => {
    setBusy(true);
    try {
      const r = await api.post('/evenements', { type });
      setEvent(r);
      setEventValues(Object.fromEntries(r.profile_fields.map((f) => [f.key, f.value])));
      if (r.tasks_created) toast.success(`${r.tasks_created} démarche(s) ajoutée(s) à Mes actions`);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Impossible de préparer tes démarches'));
    } finally {
      setBusy(false);
    }
  };

  const saveEventProfile = async () => {
    try {
      await api.patch('/profil', { values: eventValues });
      toast.success('Profil mis à jour');
    } catch (e) {
      toast.error(apiErrorMessage(e, "Impossible d'enregistrer"));
    }
  };

  if (!data) return <PageShell title="Mes démarches"><Loader2 className="animate-spin" /></PageShell>;

  return (
    <PageShell
      title="Mes démarches"
      subtitle="Skipya prépare ton dossier de A à Z, te redirige vers le site officiel pour le dépôt, puis suit ta demande jusqu'à la réponse."
      testId="demarches-page"
    >
      <div className="grid md:grid-cols-2 gap-5 mb-6">
        {data.procedures.map((p) => (
          <div key={p.id} className={`border rounded-2xl p-5 flex flex-col ${tone.panel}`} data-testid={`procedure-${p.id}`}>
            <p className="text-xs font-semibold text-[#81d8d0] mb-1">{p.organisme}</p>
            <h2 className="text-xl font-semibold mb-2">{p.title}</h2>
            <p className={`text-sm leading-6 mb-4 flex-1 ${tone.muted}`}>{p.summary}</p>
            {p.dossier ? (
              <>
                <div className="flex items-center justify-between mb-2">
                  <StatusBadge status={p.dossier.status} />
                  <span className="text-sm font-semibold">{p.dossier.progress_percent} %</span>
                </div>
                <ProgressBar value={p.dossier.progress_percent} className="mb-3" />
                <p className={`text-sm mb-4 ${tone.muted}`}>Prochaine étape : {p.dossier.next_action}</p>
                <Link to={`/dashboard/demarches/${p.dossier.id}`} className={primaryButton}>
                  Continuer mon dossier <ArrowRight size={16} />
                </Link>
              </>
            ) : (
              <button type="button" className={primaryButton} disabled={busy} onClick={() => start(p.id)}>
                Préparer mon dossier <ArrowRight size={16} />
              </button>
            )}
            <a href={p.info_url} target="_blank" rel="noreferrer" className={`mt-3 text-xs inline-flex items-center gap-1 ${tone.muted}`}>
              Règles officielles (service-public.fr) <ExternalLink size={12} />
            </a>
          </div>
        ))}
      </div>

      <Panel title="Ta situation a changé ?" testId="life-events">
        <p className={`text-sm mb-4 ${tone.muted}`}>Indique ce qui vient de t'arriver : Skipya te propose toutes les démarches qui en découlent.</p>
        <div className="flex flex-wrap gap-2">
          {data.events.map((e) => (
            <button key={e.type} type="button" disabled={busy} onClick={() => declare(e.type)}
              className={`${secondaryButton} ${event?.event.type === e.type ? 'bg-[#81d8d0]/15' : ''}`}>
              {e.label}
            </button>
          ))}
        </div>

        {event && (
          <div className={`mt-5 rounded-xl p-4 ${tone.soft}`} data-testid="life-event-result">
            <p className="font-semibold mb-3 flex items-center gap-2"><Sparkles size={16} className="text-[#81d8d0]" /> {event.event.label} : voici ce qu'il faut faire</p>
            {event.procedures.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 py-2">
                <span className="text-sm"><strong>{p.title}</strong> : Skipya prépare le dossier</span>
                <button type="button" className={primaryButton} disabled={busy} onClick={() => start(p.id)}>Préparer</button>
              </div>
            ))}
            <ul className="mt-2 space-y-2">
              {event.tasks.map((t) => (
                <li key={t.title} className="text-sm flex gap-2">
                  <CheckCircle2 size={16} className="text-[#81d8d0] shrink-0 mt-0.5" />
                  <span>
                    <strong>{t.title}</strong> : {t.description}{' '}
                    {t.url && (isInternal(t.url)
                      ? <Link to={t.url} className="underline">Ouvrir</Link>
                      : <a href={t.url} target="_blank" rel="noreferrer" className="underline">Site officiel</a>)}
                  </span>
                </li>
              ))}
            </ul>
            <p className={`text-xs mt-3 ${tone.muted}`}>Ces démarches sont aussi dans « Mes actions ».</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              {event.profile_fields.map((f) => (
                <FieldInput key={f.key} field={f} value={eventValues[f.key]}
                  onChange={(v) => setEventValues((prev) => ({ ...prev, [f.key]: v }))} />
              ))}
            </div>
            <button type="button" className={`${secondaryButton} mt-4`} onClick={saveEventProfile}>Mettre mon profil à jour</button>
          </div>
        )}
      </Panel>
    </PageShell>
  );
};

export default DemarchesPage;
